/**
 * businessDomain → 목록 페이지 경로 매핑 (전역 검색 등에서 사용)
 */

import {
  isLegacyMarketingDomain,
  LEGACY_MARKETING_DOMAIN_LIST_PATHS,
  resolveLegacyMarketingDomainDetailPath,
} from '@shared/marketing-appeal/fe-route-map';
import {
  itemSubRoutes,
  financeSubRoutes,
  financeAdvancedSubRoutes,
  spaceSubRoutes,
  knowledgeSubRoutes,
  appealSubRoutes,
  calendarSubRoutes,
  eventSubRoutes,
  logSubRoutes,
  manufacturingSubRoutes,
  type DomainSubRoute,
} from '../config/domainRouteConfig';

const ENTITY_DOMAIN_PATHS: Record<string, string> = {
  internal_entity: '/entity/internal',
  external_entity: '/entity/external',
  user_entity: '/entity/user',
};

/** When siblings share businessDomain, prefer route whose path matches subdomain suffix (e.g. finance_state → state). */
function pickPrimaryListRoute(
  businessDomain: string,
  routes: readonly DomainSubRoute[]
): DomainSubRoute {
  const first = routes.at(0);
  if (!first) {
    throw new Error(`pickPrimaryListRoute: no routes for ${businessDomain}`);
  }
  if (routes.length === 1) {
    return first;
  }
  const suffix = businessDomain.includes('_')
    ? businessDomain.slice(businessDomain.indexOf('_') + 1)
    : '';
  return routes.find((route) => route.path === suffix) ?? first;
}

function buildDomainPathMap(): Record<string, string> {
  const map: Record<string, string> = { ...ENTITY_DOMAIN_PATHS };
  const groups: [string, readonly DomainSubRoute[]][] = [
    ['item', itemSubRoutes],
    ['finance', financeSubRoutes],
    ['finance-advanced', financeAdvancedSubRoutes],
    ['space', spaceSubRoutes],
    ['knowledge', knowledgeSubRoutes],
    ['appeal', appealSubRoutes],
    ['calendar', calendarSubRoutes],
    ['event', eventSubRoutes],
    ['log', logSubRoutes],
    ['manufacturing', manufacturingSubRoutes],
  ];

  for (const [base, routes] of groups) {
    const routesByDomain = new Map<string, DomainSubRoute[]>();
    for (const route of routes) {
      const siblings = routesByDomain.get(route.businessDomain) ?? [];
      siblings.push(route);
      routesByDomain.set(route.businessDomain, siblings);
    }

    for (const [businessDomain, siblings] of routesByDomain) {
      const primary = pickPrimaryListRoute(businessDomain, siblings);
      map[businessDomain] = `/${base}/${primary.path}`;
    }
  }

  return map;
}

const domainListPathMap = buildDomainPathMap();

const legacyDomainAliases: Record<string, string> = {
  calendar_event: '/calendar/event',
  ...LEGACY_MARKETING_DOMAIN_LIST_PATHS,
};

/**
 * 비즈니스 도메인에 해당하는 목록 페이지 경로 반환
 */
export function getListPathForBusinessDomain(businessDomain: string): string {
  return domainListPathMap[businessDomain] ?? legacyDomainAliases[businessDomain] ?? '/entity';
}

const SPACE_BUSINESS_DOMAINS = new Set(spaceSubRoutes.map((route) => route.businessDomain));

/** Space subdomain keys that support list → detail deep links from workspace Objects panel. */
export function isSpaceBusinessDomain(businessDomain: string): boolean {
  return SPACE_BUSINESS_DOMAINS.has(businessDomain);
}

/**
 * 비즈니스 도메인 + 레코드 ID → 상세 페이지 경로 (GlobalSearch · Workspace Objects SSOT)
 */
export function getDetailPathForBusinessDomain(businessDomain: string, recordId: string): string {
  const trimmedId = recordId.trim();
  if (trimmedId.length === 0) {
    return getListPathForBusinessDomain(businessDomain);
  }

  if (isLegacyMarketingDomain(businessDomain)) {
    return resolveLegacyMarketingDomainDetailPath(businessDomain, trimmedId);
  }

  const listPath = getListPathForBusinessDomain(businessDomain);
  if (listPath === '/entity') {
    return listPath;
  }
  return `${listPath}/${trimmedId}`;
}

/**
 * Workspace Objects row href — space domains with a scoped record id link to detail; others stay on list.
 */
export function getWorkspaceObjectLinkPath(objectKey: string, recordId?: string): string {
  if (isSpaceBusinessDomain(objectKey) && recordId?.trim()) {
    return getDetailPathForBusinessDomain(objectKey, recordId);
  }
  return getListPathForBusinessDomain(objectKey);
}
